// src/redux/companySlice.js
import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  companies: [],
  company: null,
  loading: false,
  isSuccess: false,
  message: '',
  errors: [],
  favoris: {},

  // Lost items
  lostItems: [],
  lostLoading: false,
  lostError: '',

  // Jobs / Offres
  jobs: [],
  jobDetails: null,
  jobsLoading: false,
  jobsError: '',

  // Companies by country
  countryCompanies: [],
  countryLoading: false,
  countryError: '',

  // Business areas
  businessAreas: [],
  businessAreasLoading: false,
  businessAreasError: '',

  // Companies by business area
  businessAreaCompanies: [],
  businessAreaCompaniesLoading: false,
  businessAreaCompaniesError: '',

  // Best deals (toutes entreprises confondues, offres non expirées)
  bestDeals: [],
  bestDealsLoading: false,
  bestDealsError: ''
};

export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/getAll');
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyByName = createAsyncThunk(
  'company/fetchCompanyByName',
  // `lang` (suffixe ISO : fr, ar, es…) demande au backend la fiche traduite. À ne passer que
  // pour l'affichage : la réponse remplace alors company_name par sa traduction.
  async ({ message, lang }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/getByName/${message}`, {
        params: lang ? { lang } : undefined
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'company/fetchCompanyById',
  async ({ companyid }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/${companyid}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addFavoris = createAsyncThunk(
  'company/addFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/user-favoris/add-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeFavoris = createAsyncThunk(
  'company/removeFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/user-favoris/remove-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFavoris = createAsyncThunk(
  'company/getFavoris',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/user-favoris/get-liste-favoris`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// `code` is the normalized country code (e.g. "TN") returned by GET /country — matching by
// code instead of raw text means it works no matter which language/spelling a company's
// `country` field was stored in. Optional userLat/userLng sort results by distance.
export const fetchCompaniesByCountry = createAsyncThunk(
  'company/fetchCompaniesByCountry',
  async ({ code, userLat, userLng }, { rejectWithValue, dispatch }) => {
    try {
      const params = {};
      if (userLat != null && userLng != null) {
        params.lat = userLat;
        params.lng = userLng;
      }
      const response = await api.get(`/country/${code}/companies`, { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchBusinessAreas = createAsyncThunk(
  'company/fetchBusinessAreas',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get('/businessarea/all');
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchBestDeals = createAsyncThunk(
  'company/fetchBestDeals',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get('/company/best-deals/all');
      const companies = Array.isArray(data) ? data : data?.data || [];

      // Le back renvoie les offres groupées par entreprise (et écarte déjà celles
      // dont la date de fin est passée). On aplatit pour pouvoir trier et répartir
      // l'ensemble des offres en une seule passe, en gardant sur chaque offre de
      // quoi afficher l'entreprise d'origine.
      return companies.flatMap((c) =>
        (c.bestDeals || []).map((deal) => ({
          ...deal,
          company: {
            _id: c.companyId,
            company_name: c.company_name,
            company_name_ar: c.company_name_ar,
            company_name_de: c.company_name_de,
            company_name_en: c.company_name_en,
            company_name_es: c.company_name_es,
            company_name_fr: c.company_name_fr,
            company_name_pt: c.company_name_pt,
            photoprincipal: c.photoprincipal
          },
          business_area: c.business_area
        }))
      );
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompaniesByBusinessArea = createAsyncThunk(
  'company/fetchCompaniesByBusinessArea',
  async ({ businessAreaId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get('/company/getAll');
      const allCompanies = data?.data || (Array.isArray(data) ? data : []);
      return allCompanies.filter(
        (company) => company.business_area?._id === businessAreaId
      );
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyLostItems = createAsyncThunk(
  'company/fetchCompanyLostItems',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/company/lost-items/company/${companyId}`);
      return Array.isArray(data) ? data : data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== ✅ 1. Liste des offres ===================== 
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async (
    { companyId, status, sortBy = 'createdAt', order = 'desc' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const query = new URLSearchParams();
      if (Array.isArray(status) && status.length) {
        status.forEach((s) => query.append('status', s));
      }
      if (sortBy) query.set('sortBy', sortBy);
      if (order) query.set('order', order);
      const qs = query.toString();

      const url = qs
        ? `/collaboration/offers/company/${companyId}?${qs}`
        : `/collaboration/offers/company/${companyId}`;

      const { data } = await api.get(url);
      return Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) && data.data) || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
*/
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/job-posts/company/${companyId}`);
      return Array.isArray(data) ? data : data?.data || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
/* ===================== ✅ 2. Détails d'une offre ===================== */
export const fetchCompanyOfferDetails = createAsyncThunk(
  'company/fetchCompanyOfferDetails',
  async ({ companyId, offerId }, { rejectWithValue, dispatch }) => {
    try {
      const url = `/collaboration/offers/company/${companyId}/${offerId}`;
      const { data } = await api.get(url);
      return data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== Slice ===================== */

const companiesSlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* --- Companies --- */
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.companies = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchCompanies.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Company By ID --- */
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.company = payload || null;
        state.loading = false;
      })
      .addCase(fetchCompanyById.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Favoris --- */
      .addCase(getFavoris.fulfilled, (state, { payload }) => {
        state.favoris = payload || null;
      })

      /* --- Lost Items --- */
      .addCase(fetchCompanyLostItems.pending, (state) => {
        state.lostLoading = true;
      })
      .addCase(fetchCompanyLostItems.fulfilled, (state, { payload }) => {
        state.lostLoading = false;
        state.lostItems = payload || [];
      })
      .addCase(fetchCompanyLostItems.rejected, (state, { payload }) => {
        state.lostLoading = false;
        state.lostError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- ✅ Jobs / Offers --- */
      .addCase(fetchCompanyOffers.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = '';
      })
      .addCase(fetchCompanyOffers.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobs = payload || [];
      })
      .addCase(fetchCompanyOffers.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement des offres';
      })

      /* --- Companies by country --- */
      .addCase(fetchCompaniesByCountry.pending, (state) => {
        state.countryLoading = true;
        state.countryError = '';
        state.countryCompanies = [];
      })
      .addCase(fetchCompaniesByCountry.fulfilled, (state, { payload }) => {
        state.countryLoading = false;
        state.countryCompanies = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchCompaniesByCountry.rejected, (state, { payload }) => {
        state.countryLoading = false;
        state.countryError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- Business areas --- */
      .addCase(fetchBusinessAreas.pending, (state) => {
        state.businessAreasLoading = true;
        state.businessAreasError = '';
      })
      .addCase(fetchBusinessAreas.fulfilled, (state, { payload }) => {
        state.businessAreasLoading = false;
        state.businessAreas = payload;
      })
      .addCase(fetchBusinessAreas.rejected, (state, { payload }) => {
        state.businessAreasLoading = false;
        state.businessAreasError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- Companies by business area --- */
      .addCase(fetchCompaniesByBusinessArea.pending, (state) => {
        state.businessAreaCompaniesLoading = true;
        state.businessAreaCompaniesError = '';
        state.businessAreaCompanies = [];
      })
      .addCase(fetchCompaniesByBusinessArea.fulfilled, (state, { payload }) => {
        state.businessAreaCompaniesLoading = false;
        state.businessAreaCompanies = payload;
      })
      .addCase(fetchCompaniesByBusinessArea.rejected, (state, { payload }) => {
        state.businessAreaCompaniesLoading = false;
        state.businessAreaCompaniesError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- Best deals --- */
      .addCase(fetchBestDeals.pending, (state) => {
        state.bestDealsLoading = true;
        state.bestDealsError = '';
      })
      .addCase(fetchBestDeals.fulfilled, (state, { payload }) => {
        state.bestDealsLoading = false;
        state.bestDeals = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchBestDeals.rejected, (state, { payload }) => {
        state.bestDealsLoading = false;
        state.bestDealsError = payload?.message || 'Erreur lors du chargement des offres';
      })

      /* --- ✅ Job Offer Details --- */
      .addCase(fetchCompanyOfferDetails.pending, (state) => {
        state.jobsLoading = true;
        state.jobDetails = null;
      })
      .addCase(fetchCompanyOfferDetails.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobDetails = payload || null;
      })
      .addCase(fetchCompanyOfferDetails.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement du détail';
      });
  }
});

export default companiesSlice.reducer;
